import { Card, CardContent } from "./ui/card";
import { avgText } from "../lib/recordFigures";
import { counterText } from "../lib/counters";

/**
 * Issue #460 — the three career dashboards, extracted from
 * `pages/Statistics.jsx` so the coach's Career snapshot renders the SAME
 * implementation the player sees, not a fork of it.
 *
 * Martin: *"Career snapshot should have the same filters as per the player
 * profile and should include 'Career highlights', 'Bowling record' and
 * 'Fielding & Wicket Keeping' dashboard."* Those three headings are these
 * three sections, verbatim from the Statistics page — which is why this is
 * an EXTRACTION and not a new component: a copy in CoachPortal would show
 * a coach numbers that drift from the player's the first time either side
 * is edited alone. `CareerStatSections.test.js` pins that these headings
 * exist in this file only.
 *
 * Purely presentational: `d` is a `/stats/summary` payload, already
 * filtered by the server (issues #55/#78 — see `lib/statsQuery.js`). Which
 * player it describes is the CALLER's contract: the Statistics page reads
 * the session player, the coach snapshot passes `player_id` and the API's
 * `resolveScope` enforces the active-link check.
 */
export default function CareerStatSections({ d }) {
  return (
    <>
      {/* Career highlights */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Career highlights</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatTile label="Matches" value={d.matches} color="#0B2E59" tid="stat-matches" />
          <StatTile label="Innings" value={d.innings} color="#0B2E59" tid="stat-innings" />
          <StatTile label="Runs" value={d.total_runs} color="#0B2E59" tid="stat-runs" />
          <StatTile label="Not Outs" value={d.not_outs} color="#64748B" tid="stat-notouts" />
          <StatTile label="Average" value={avgText(d.avg)} color="#D4A017" tid="stat-avg" />
          <StatTile label="Strike Rate" value={counterText(d.sr)} color="#84C5FF" tid="stat-sr" />
          <StatTile label="Highest" value={d.highest} color="#0B2E59" tid="stat-highest" />
          <StatTile label="50s" value={d.fifties} color="#D4A017" tid="stat-50s" />
          <StatTile label="100s" value={d.hundreds} color="#D4A017" tid="stat-100s" />
          <StatTile label="4s" value={d.fours} color="#64748B" tid="stat-4s" />
          <StatTile label="6s" value={d.sixes} color="#64748B" tid="stat-6s" />
          <StatTile label="Wickets" value={d.total_wickets} color="#84C5FF" tid="stat-wickets" />
        </div>
      </div>

      {/* Fielding row */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Fielding & wicket-keeping</p>
        {/* #727. Run outs and assists are now two counters each, not one
            shared between the disciplines — a keeper's run out at the
            stumps and a fielder's from the covers stopped being the same
            number when Martin said "Separate them" (2026-09-18). The (WK)
            tiles are where the keeper's half lands; without them the split
            would capture a figure nothing ever shows, which is #706's
            defect under a new name. The unmarked tiles are the fielder's,
            and their historical values are unchanged — no backfill moved a
            past match from one side to the other. */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatTile label="Catches" value={d.catches} color="#0B2E59" tid="stat-catches" />
          <StatTile label="Run Outs (fielding)" value={d.run_outs} color="#D4A017" tid="stat-runouts" />
          <StatTile label="Direct Hits" value={d.direct_hit_run_outs} color="#D4A017" tid="stat-directhits" />
          <StatTile label="Assists" value={d.assists} color="#64748B" tid="stat-assists" />
          <StatTile label="Stumpings" value={d.stumpings} color="#84C5FF" tid="stat-stumpings" />
          <StatTile label="Byes Conc." value={d.byes_conceded} color="#64748B" tid="stat-byes" />
          <StatTile label="Run Outs (WK)" value={d.wicketkeeping_run_outs} color="#84C5FF" tid="stat-wk-runouts" />
          <StatTile label="Assists (WK)" value={d.wicketkeeping_assists} color="#84C5FF" tid="stat-wk-assists" />
        </div>
      </div>

      {/* Bowling extras */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Bowling record</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatTile label="Wickets" value={d.total_wickets} color="#D4A017" tid="stat-wickets-2" />
          <StatTile label="Economy" value={counterText(d.econ)} color="#84C5FF" tid="stat-econ" />
          <StatTile label="Maidens" value={d.maidens} color="#0B2E59" tid="stat-maidens" />
          <StatTile label="Dot Balls" value={d.dot_balls} color="#0B2E59" tid="stat-dots" />
          <StatTile label="Wides" value={d.wides} color="#64748B" tid="stat-wides" />
          <StatTile label="No Balls" value={d.no_balls} color="#64748B" tid="stat-nb" />
        </div>
      </div>
    </>
  );
}

// Moved with its sections from Statistics.jsx. The dash-on-failure contract
// this feeds (journey 8: a failed load renders "—", never a plausible "0")
// is upstream of here for every OTHER tile — a failed load never reaches
// these tiles at all. Strike Rate and Economy are the one exception (issue
// #981, D190.1). `computeStatsSummary` now sends `sr`/`econ` as `null` for
// a loaded player with no base (a non-batter/non-bowler) — but D190.1
// (round 2 of #985, correcting D104's "old screens keep two until
// rebuilt") is explicit that THIS shipped screen keeps today's exact
// display for every value it already has: no reformatting, so a strike
// rate of 104.57 stays "104.57" and a whole-number economy of 5 stays "5",
// byte-for-byte what `main` renders today. `counterText` is a bare null
// guard around that unchanged rendering — never `srText`/`toRateText`
// (those belong to `CoachPlayerCareerSnapshot.jsx`'s rebuilt tiles only,
// which D190.1 leaves on the one/two-decimal house format). The ONLY
// visible change on this screen is "—" where a non-bowler/non-batter used
// to show a plausible "0"/"0.00".
function StatTile({ label, value, color, tid }) {
  return (
    <Card className="border-slate-200" data-testid={tid}>
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <div className="font-display font-black text-2xl mt-1" style={{ color }}>{value}</div>
      </CardContent>
    </Card>
  );
}
